<template>
    <div id="preferred-agents">
        <div class="preferred-agents-container" v-if="!modalEnabled">
            <span
                class="preferred-agents-element"
                v-for="agent of getSelectedAgents()"
                :style="{
                    borderColor: `${agent.color} !important`,
                }"
                v-tippy="{
                    followCursor: true,
                    placement: 'bottom-end',
                }"
                :content="agent.name"
                :key="agent._id"
            >
                <div class="circle" :style="{ backgroundColor: isDarkMode ? 'white' : 'black' }"></div>
                <div @click="visibleModal = true">
                    {{ agent.name.slice(0, 15) + (agent.name.length >= 15 ? "..." : "") }}
                </div>
            </span>
            <button class="add-dashed-btn" @click="visibleModal = true" v-if="!readOnly">
                <span class="material-icons">add</span>
            </button>
        </div>
        <!-- <PreferredRepresentativeModal
            v-if="visibleModal"
            :visitorId="visitorId"
            v-model="selectedAgentIds"
            @cancel="visibleModal = false"
        />-->
        <FormModal
            :title="$t('message.assignPreferredAgent')"
            :subtitle="$t('message.representativeAssignedToTheVisitor')"
            v-if="modalEnabled || visibleModal"
            v-model="selectedAgentIds"
            @cancel="() => (modalEnabled ? $emit('cancel') : (visibleModal = false))"
        >
            <template #body>
                <div class="pf-main">
                    <div class="pr-search">
                        <input
                            :placeholder="$t('message.searchRepresentative')"
                            v-model="searchText"
                            @input="searchChange"
                        />
                        <span class="material-icons mx-2 pr-icon"> search </span>
                    </div>
                    <hr />
                    <div class="pr-items pr-item-wrapper">
                        <div class="pr-item" v-for="agent of filterUsers" @click="toggleAgent(agent._id)">
                            <a-checkbox class="segment-checkbox" :checked="selectedAgentIds.includes(agent._id)" />

                            <div class="user-data-wrapper">
                                <div class="user-data">
                                    <Avatar
                                        :size="30"
                                        :src="agent.gravatar"
                                        :username="agent.name"
                                        class="mr-2 avatar"
                                    />
                                    <div class="metas">
                                        <div class="pr-item-name">{{ agent.name }}</div>
                                        <div>
                                            {{ getUserDepartmentNames(agent) }}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="pr-item-status"
                                    :data-online="$_.get(user, '_id') === agent._id ? true : agent.isOnline"
                                    :data-status="agent.status.toLowerCase()"
                                >
                                    <div class="pr-item-status-circle"></div>
                                    {{ agent.status }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="actions">
                    <a-button @click="handleCancel"> {{ $t("message.cancel") }}</a-button>
                    <a-button class="pr-confirm-btn" @click="handleConfirm" type="primary" :disabled="!isChanged()">
                        {{ $t("message.save") }}
                    </a-button>
                </div>
            </template>
        </FormModal>
    </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import PreferredRepresentativeModal from "./PreferredAgentsFormModal.vue";
import log2 from "../utils/log2";
import FormModal from "./FormModal.vue";
import Templates from "../views/Pages/Chatbot/pages/templates/Templates.vue";
import Avatar from "vue-avatar";
import { sendSocketMessage } from "../utils/socketStream";

const log = log2("PreferredAgents");
export default {
    name: "PreferredAgents",
    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
        visitorId: {
            type: String,
            required: true,
        },
        footerVisibility: {
            type: Boolean,
            default: true,
        },
        value: {
            type: Array,
            required: false,
            default: () => [],
        },
        modalEnabled: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        Templates,
        PreferredRepresentativeModal,
        FormModal,
        Avatar,
    },
    emits: ["cancel"],
    data({ value }) {
        return {
            searchText: null,
            selectedAgentIds: _.cloneDeep(value || []),
            search: "",
            visibleModal: false,
        };
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            users: (state) => state.user.users.data,
            filterUsers: (state) => _.cloneDeep(state.user.users.data),
            user: (state) => state.auth.user,
            subjects: (state) => state.subject.subjects.data,
            site: (state) => state.site.site,
        }),
        filterUsers() {
            return this.users.filter((x) => new RegExp(this.search, "gi").test(x.name));
        },
    },
    methods: {
        ...mapActions({
            updatePreferredAgents: "visitor/updatePreferredAgents",
        }),
        getUserDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.subjects
                      .filter((x) => (user.subjects || []).includes(x._id))
                      .map(
                          (x) =>
                              _.get(x, `languageContent.${this.$i18n.locale}.name`) ||
                              _.get(x, `languageContent.${Object.keys(x.languageContent)[0]}.name`)
                      )
                      .join(",")
                      .toLowerCase()
                : this.$t("message.allDepartments");
        },
        async save() {
            return await this.updatePreferredAgents({
                visitorId: this.visitorId,
                userIds: this.selectedAgentIds,
            }).then((data) => {
                sendSocketMessage("user sync-visitor", {
                    siteId: this.site._id,
                    visitorId: this.visitorId,
                });
                return data;
            });
        },
        toggleAgent(agentId) {
            this.selectedAgentIds = this.selectedAgentIds.includes(agentId)
                ? this.selectedAgentIds.filter((x) => x !== agentId)
                : [...this.selectedAgentIds, agentId];
        },
        searchChange() {
            this.search = this.searchText;
        },
        async handleConfirm() {
            await this.save();
            this.$emit("input", this.selectedAgentIds);
            this.visibleModal = false;
            this.$emit("cancel");
        },
        handleCancel() {
            this.visibleModal = false;
            this.$emit("cancel");
        },
        isChanged() {
            return !_.isEqual(this.selectedAgentIds, this.value);
        },
        getSelectedAgents() {
            return (this.users || []).filter((u) => this.selectedAgentIds.includes(u._id));
        },
    },
    watch: {
        value: {
            handler: function (val) {
                if (_.isEqual(val, this.selectedAgentIds)) return;
                this.selectedAgentIds = _.cloneDeep(val || []);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.preferred-agents-container {
    display: flex;
    flex-wrap: wrap;

    .preferred-agents-element {
        border-radius: 10px;
        border-style: solid;
        color: black;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 1px;
        padding: 0px 4px;

        .circle {
            width: 10px;
            height: 10px;
            margin-right: 5px;
            border-radius: 100%;
        }
    }
    .add-dashed-btn {
        width: 24px;
        height: 24px;
        border-radius: 4px;
        border: 1px dashed #6f63f2;
        background-color: #fff;
        color: #6f63f2;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background-color: #7561f333;
            transform: scale(1.05);
        }

        .material-icons {
            font-size: 16px;
        }
    }
}

.dark-mode .preferred-agents-element {
    color: white;
}
</style>

<style lang="scss" scoped>
.classic-button {
    border: none;
    background-color: #ffa80040 !important;
    color: #ffa800 !important;
    font-family: Poppins;
    font-weight: 600 !important;
    font-size: 14px;
    cursor: pointer;
    padding: 3px 10px;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.pf-main {
    display: flex;
    flex-direction: column;
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    padding: 5px;

    .pr-search {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 100%;

        input {
            border: 1px solid #c6cdd4 !important;
            width: 100%;
            padding: 10px;
            padding-right: 40px;
            border-radius: 5px;
            transition: all 0.3s;
        }

        input:focus {
            border: 1px solid black !important;
        }

        .pr-icon {
            position: absolute;
            right: 4px;
        }
    }

    .pr-items {
        min-height: 300px;
        max-height: 300px;
        overflow: auto;

        .pr-item:last-child {
            border-bottom: none;
        }

        .pr-item {
            display: flex;
            justify-content: start;
            align-items: center;
            flex-wrap: nowrap;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            gap: 10px;
            border-bottom: 1px solid var(--color-border-default);

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            .user-data-wrapper {
                width: 100%;
                display: flex;
                justify-content: space-between;

                padding: 0 5px;

                .user-data {
                    display: flex;
                    gap: 5px;
                    align-items: center;

                    .avatar {
                        color: var(--color-text-onprimary-default);
                    }

                    .metas {
                        display: flex;
                        flex-direction: column;
                    }
                }
            }

            .pr-item-status {
                display: flex;
                justify-content: center;
                align-items: center;

                .pr-item-status-circle {
                    width: 10px;
                    height: 10px;
                    border-radius: 100%;
                    margin-right: 2px;
                }

                &[data-online="true"] {
                    &[data-status="online"] {
                        color: #1bc5bd !important;

                        .pr-item-status-circle {
                            background: #1bc5bd !important;
                        }
                    }

                    &[data-status="away"] {
                        color: #3699ff !important;

                        .pr-item-status-circle {
                            background: #3699ff !important;
                        }
                    }

                    &[data-status="busy"] {
                        color: #ffa829 !important;

                        .pr-item-status-circle {
                            background: #ffa829 !important;
                        }
                    }

                    &[data-status="offline"] {
                        color: #f64e60 !important;

                        .pr-item-status-circle {
                            background: #f64e60 !important;
                        }
                    }
                }

                &:not([data-online]) {
                    &[data-status="online"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="away"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="busy"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="offline"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }
                }
            }
        }
    }
}

.ant-modal-root.v2 {
    .ant-modal-close-x {
        position: absolute;
        color: white;
        font-size: 20px;
        width: 30px;
        height: 30px;
        line-height: 30px;
        border-radius: 4px;

        &:hover {
            background: var(--color-bg-ghost-hover);
        }
    }

    .ant-modal-body {
        padding: 0 !important;
        font-size: 14px;
        font-weight: 400;
        color: var(--color-text-default);
        line-height: 24px;
    }

    .ant-modal-content {
        border-radius: 4px;
    }
}
</style>
